import { DataGrid, GridColDef, GridValidRowModel } from "@mui/x-data-grid";
import { useMemo } from "react";

interface IProps<TRow extends GridValidRowModel> {
  rows: TRow[];
  columns: GridColDef[];
  maxHeight?: string;
  // jsdom has no viewport, so tests opt out of virtualization.
  disableVirtualization?: boolean;
}

// Sorting stays with SortableTableHeader and the url params; the grid only paints.
const EndpointsDataGrid = <TRow extends GridValidRowModel,>({ rows, columns, maxHeight = "65vh", disableVirtualization = false }: IProps<TRow>) => {
  const firstField = columns[0]?.field;

  // Row fields are not unique: the same endpoint repeats per package and rule.
  const indexedRows = useMemo(() => rows.map((row, index) => ({ ...row, gridRowIndex: index })), [rows]);
  return (
    <DataGrid
      rows={indexedRows}
      columns={columns}
      getRowId={(row) => row.gridRowIndex}
      // The MIT grid forces pagination on and caps a page at 100; server mode hands it everything.
      paginationMode="server"
      rowCount={indexedRows.length}
      disableVirtualization={disableVirtualization}
      getRowHeight={() => "auto"}
      columnHeaderHeight={60}
      hideFooter
      disableRowSelectionOnClick
      disableColumnMenu
      // Ten columns only, so rendering them all keeps the first one pinnable with css.
      columnBufferPx={4000}
      sx={{
        maxHeight,
        border: "none",
        color: "var(--dialog-title-color)",
        fontSize: "14px",
        // Without this the row rule falls back to the MUI theme border and renders light.
        "--DataGrid-rowBorderColor": "var(--dialog-border-color)",
        "--rowBorderColor": "var(--dialog-border-color)",
        "& .MuiDataGrid-columnHeaders": { borderRadius: "4px" },
        "& .MuiDataGrid-columnHeader": {
          backgroundColor: "var(--background-second)",
          padding: "8px",
          borderBottom: "1px solid var(--dialog-border-color)",
        },
        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
        // Values taken from the computed styles of the original table.
        "& .MuiDataGrid-columnHeader a": {
          color: "var(--link-color)",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "20px",
        },
        "& .MuiDataGrid-columnHeader i": {
          color: "var(--dialog-title-color)",
          fontWeight: 600,
          fontSize: "14px",
          lineHeight: "20px",
        },
        // Header content is SortableTableHeader, so the grid title and icon are unused.
        "& .MuiDataGrid-columnHeaderTitleContainerContent": { overflow: "visible" },
        "& .MuiDataGrid-sortIcon, & .MuiDataGrid-iconButtonContainer": { display: "none" },
        "& .MuiDataGrid-cell": {
          backgroundColor: "var(--background-second)",
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "21px",
          padding: "8px",
          borderBottom: "1px solid var(--dialog-border-color)",
          whiteSpace: "normal",
          alignItems: "flex-start",
        },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
        "& .MuiDataGrid-cell a": { color: "var(--link-color)" },
        // Same hover treatment as .clickable in index.sass.
        "& .MuiDataGrid-cell a:hover, & .MuiDataGrid-columnHeader a:hover": {
          textDecoration: "underline",
          textDecorationColor: "var(--link-color)",
        },
        "& .MuiDataGrid-row:hover": { backgroundColor: "var(--background-second)" },
        // .state-text is inline and loses its pill shape in a flex cell.
        '& .MuiDataGrid-cell[data-field="state"]': { overflow: "visible" },
        "& .MuiDataGrid-cell .state-text": {
          display: "inline-block",
          padding: "1px 10px 2px",
          whiteSpace: "nowrap",
        },
        // Mirrors .sticky-table first-child; data-field because fillers break :first-of-type.
        [`& .MuiDataGrid-columnHeader[data-field="${firstField}"], & .MuiDataGrid-cell[data-field="${firstField}"]`]: {
          position: "sticky",
          left: 0,
          zIndex: 2,
          borderRight: "2px solid var(--dialog-border-color)",
          background: "var(--background-second)",
        },
        [`& .MuiDataGrid-columnHeader[data-field="${firstField}"]`]: { zIndex: 3 },
        "& .MuiDataGrid-columnSeparator": { display: "none" },
        // v7 pads the header row to line up with the scrollbar; unstyled it renders light.
        "& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--header": {
          backgroundColor: "var(--background-second)",
          borderBottom: "1px solid var(--dialog-border-color)",
        },
        "& .MuiDataGrid-filler": { backgroundColor: "var(--background)" },
        "& .MuiDataGrid-scrollbar": { zIndex: 4 },
      }}
    />
  );
};

export default EndpointsDataGrid;
